import {
  OnInit,
  OnChanges,
  Directive,
  Input,
  HostBinding,
  Renderer2,
  ElementRef,
  SimpleChanges
} from "@angular/core";

function uuidv4() {
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, function (c) {
    var r = (Math.random() * 16) | 0,
      v = c == "x" ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

@Directive({
    selector: "[appLoading]",
    standalone: false
})
export class LoadingDirective implements OnInit, OnChanges {
  @HostBinding("style.position")
  hostPosition: string = "relative";

  @Input() appLoading: boolean = false;
  @Input() loaderColor: string = "#e4e4e4";
  @Input() fixedLoader: boolean = false;
  @Input() helpTabLoader: boolean = false;
  @Input() loadingMessage: string = "Loading...";

  uid: string;

  constructor(private targetEl: ElementRef, private renderer: Renderer2) { }

  ngOnInit() {
    if (this.helpTabLoader)
      this.hostPosition = 'initial';
    this.uid = "loading-container-" + uuidv4();

    // Main loading container
    const loadingContainer = this.renderer.createElement("div");
    this.renderer.setStyle(loadingContainer, "display", this.appLoading ? "flex" : "none");
    this.renderer.setStyle(loadingContainer, "justify-content", "center");
    this.renderer.setStyle(loadingContainer, "align-items", "center");
    this.renderer.setStyle(loadingContainer, "flex-direction", "column");
    this.renderer.addClass(loadingContainer, this.uid);
    this.renderer.setStyle(loadingContainer, "position", 'absolute');
    this.renderer.setStyle(loadingContainer, "top", "0");
    this.renderer.setStyle(loadingContainer, "left", "0");
    this.renderer.setStyle(loadingContainer, "background", this.loaderColor);
    this.renderer.setStyle(loadingContainer, "width", "100%");
    this.renderer.setStyle(loadingContainer, "height", "105%");
    this.renderer.setStyle(loadingContainer, "z-index", "2147483646");
    // this.renderer.setStyle(loadingContainer, "backdrop-filter", "blur(2px)");
    
    // Content wrapper for spinner and message
    const contentWrapper = this.renderer.createElement("div");
    this.renderer.setStyle(contentWrapper, "display", "flex");
    this.renderer.setStyle(contentWrapper, "flex-direction", "column");
    this.renderer.setStyle(contentWrapper, "align-items", "center");
    this.renderer.setStyle(contentWrapper, "justify-content", "center");
    this.renderer.setStyle(contentWrapper, "gap", "20px");
    this.renderer.setStyle(contentWrapper, "padding", "20px");

    // Modern spinner
    const spinner = this.renderer.createElement("div");
    this.renderer.addClass(spinner, "modern-spinner");
    this.renderer.setStyle(spinner, "width", "45px");
    this.renderer.setStyle(spinner, "height", "45px");
    this.renderer.setStyle(spinner, "border", "6px solid #f3f3f3");
    this.renderer.setStyle(spinner, "border-top", "6px solid #27a6a4");
    this.renderer.setStyle(spinner, "border-radius", "50%");
    this.renderer.setStyle(spinner, "animation", "spin 1s linear infinite");
    // this.renderer.setStyle(spinner, "box-shadow", "0 4px 12px rgba(39, 166, 164, 0.3)");
    this.renderer.setStyle(spinner, "position", "relative");
    
    // Loading message
    const messageElement = this.renderer.createElement("div");
    this.renderer.setStyle(messageElement, "color", "#666");
    this.renderer.setStyle(messageElement, "font-size", "14px");
    this.renderer.setStyle(messageElement, "font-weight", "500");
    this.renderer.setStyle(messageElement, "text-align", "center");
    this.renderer.setStyle(messageElement, "font-family", "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif");
    this.renderer.setStyle(messageElement, "letter-spacing", "0.5px");
    const messageText = this.renderer.createText(this.loadingMessage);
    this.renderer.appendChild(messageElement, messageText);

    // Assemble the loading UI
    this.renderer.appendChild(contentWrapper, spinner);
    this.renderer.appendChild(contentWrapper, messageElement);
    this.renderer.appendChild(loadingContainer, contentWrapper);

    // Add CSS animation keyframes
    this.addSpinnerStyles();

    this.renderer.appendChild(this.targetEl.nativeElement, loadingContainer);
  }

  private addSpinnerStyles() {
    // Check if styles already exist
    if (document.getElementById('modern-spinner-styles')) return;

    const style = this.renderer.createElement('style');
    this.renderer.setAttribute(style, 'id', 'modern-spinner-styles');
    const css = `
      @keyframes spin {
        0% { transform: rotate(0deg); }
        100% { transform: rotate(360deg); }
      }
      
      .modern-spinner {
        position: relative;
      }
      
      .modern-spinner::before {
        content: '';
        position: absolute;
        top: -4px;
        left: -4px;
        right: -4px;
        bottom: -4px;
        border-radius: 50%;
        // background: linear-gradient(45deg, transparent, transparent 40%, rgba(39, 166, 164, 0.1));
        animation: spin 2s linear infinite reverse;
      }
    `;
    this.renderer.appendChild(style, this.renderer.createText(css));
    this.renderer.appendChild(document.head, style);
  }
  private getTextColor(backgroundColor: string): string {
    // Define color mappings for better contrast
    const colorMap: { [key: string]: string } = {
      '#e4e4e4': '#333333',           // Dark text for light gray background
      'rgba(2, 45, 66, 0.4)': '#ffffff', // White text for dark pine green background
      'rgba(255, 255, 255, 0.95)': '#666666' // Default gray text for white background
    };

    return colorMap[backgroundColor] || '#666666';
  }

  ngOnChanges(simpleChanges: SimpleChanges) {
    if (simpleChanges.appLoading || simpleChanges.loadingMessage || simpleChanges.loaderColor || simpleChanges.fixedLoader) {
      const container = this.targetEl.nativeElement;
      const div = container.querySelector("." + this.uid);
      if (div) {
        this.renderer.setStyle(div, "display", this.appLoading ? "flex" : "none");
        this.renderer.setStyle(div, "background", this.loaderColor);
        this.renderer.setStyle(div, "position", this.fixedLoader ? 'fixed' : 'absolute');
        const contentWrapper = div.querySelector('div');
        if (contentWrapper && contentWrapper.children.length > 1) {
          const messageElement = contentWrapper.children[1];
          if (messageElement) {
            messageElement.textContent = this.loadingMessage;
            const textColor = this.getTextColor(this.loaderColor);
            this.renderer.setStyle(messageElement, "color", textColor);
          }
        }
        // Handle body scroll only if loader is fixed and visible
        if (this.fixedLoader) {
          const mainElement = document.querySelector('main');
          if (mainElement) {
            if (this.appLoading) {
              mainElement.style.overflowY = 'hidden';
            } else {
              mainElement.style.overflowY = '';
            }
          }
        }
      }
    }
  }
}